import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Paper,
  Grid,
  Box,
} from "@mui/material";
import { styles } from "../../../styles";
import { DataModal } from "./ModalData";
import { MButton, MCheckbox, ResponsiveFormContainer } from "@app/components";
import { useFormContext } from "react-hook-form";
import { ResultDataTable } from "../calculation";
import PDFGeneratorService from "@app/pages/samples/utils/PDFGenerator";
import { useParams } from "react-router-dom";
import { useSample } from "@app/pages/samples/utils/useSample";
import { toExponentialUppercaseWhitZero } from "@app/utils/functions";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import dayjs from "dayjs";
import { KeysTabsAnalysis } from "@app/types/samples.types";
import { TYPE_ANALYSIS_ALFATOTAL } from "@app/pages/samples/utils/constants";
import { useIsotopos } from "@services/servicesGenericsTables";
import { SR_ISOTOPES } from "../../SR/utils/constants";

interface ResultsTableProps {
  isCurrier?: boolean;
}

const ResultsTable: React.FC<ResultsTableProps> = ({ isCurrier = false }) => {
  const [open, setOpen] = useState(false);
  const form = useFormContext();
  const { idMuestra } = useParams();
  const sample = useSample.getState().get(Number(idMuestra));

  const { fetchCalculation, loadingResults, putResult } = useAnalysis();
  const [results, setResults] = useState<ResultDataTable[]>([]);
  const [resultsAll, setResultsAll] = useState<any>({});
  const [allAnalysisResults, setAllAnalysisResults] = useState<any>(null);
  const { isotoposList } = useIsotopos();

  const formatResults = useCallback((response: any): ResultDataTable[] => {
    return [
      { label: "ACTIVIDAD", value: Number(response.activity) },
      { label: "INCERTIDUMBRE", value: Number(response.uncertainty) },
      { label: "LID", value: Number(response.lid) },
    ];
  }, []);

  const startSamplingDate = form.watch("start_date_time");
  const endSamplingDate = form.watch("end_date_time");

  const expositionDays = useMemo(() => {
    const inicio = dayjs(startSamplingDate).startOf("day");
    const fin = dayjs(endSamplingDate).startOf("day");
    return fin.diff(inicio, "day");
  }, [startSamplingDate, endSamplingDate]);

  const formatResultsDP = useCallback(
    (response: any, diffDate): ResultDataTable[] => {
      return [
        { label: "Dosis (mSv/año)", value: Number(response.activity) },
        {
          label: "Dosis (mSv/Trim. o mes)",
          value: (Number(response.activity) / 365) * diffDate || expositionDays,
        },
        { label: "Error o", value: Number(response.uncertainty) },
        { label: "Error 2o", value: Number(response.uncertainty) * 2 },
      ];
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const preloadResults = async () => {
      const idResultPreload = form.getValues("idResultPreload");
      if (idResultPreload !== undefined) {
        const response = await fetchCalculation(idResultPreload);
        if (response) {
          form.setValue("to_report", response.to_reports ? "to_report" : "");
          setResultsAll(response);
          if (form.getValues("typeAnalysis")?.codigo === KeysTabsAnalysis.DP) {
            const inicio = dayjs(form.watch("start_date_time")).startOf("day");
            const fin = dayjs(form.watch("end_date_time")).startOf("day");
            const diffDate = fin.diff(inicio, "day");
            setResults(formatResultsDP(response, diffDate));
          } else if (
            form.getValues("typeAnalysis")?.codigo === KeysTabsAnalysis.Sr89 ||
            form.getValues("typeAnalysis")?.codigo === KeysTabsAnalysis.Sr90
          ) {
            // Add sr89, sr90 & y90 isotopes cdes to results table
            const sr89Cdes =
              isotoposList.find(
                (iso) => iso.codigo === SR_ISOTOPES.SR89.simbol
              )?.cdes || "";
            const sr90Cdes =
              isotoposList.find(
                (iso) => iso.codigo === SR_ISOTOPES.SR90.simbol
              )?.cdes || "";
            const y90Cdes =
              isotoposList.find((iso) => iso.codigo === SR_ISOTOPES.Y90.simbol)
                ?.cdes || "";

            const responseWithIsotopes = {
              ...response,
              [SR_ISOTOPES.SR89.key]: sr89Cdes,
              [SR_ISOTOPES.SR90.key]: sr90Cdes,
              [SR_ISOTOPES.Y90.key]: y90Cdes,
            };
            
            setResultsAll(responseWithIsotopes);
            setResults(formatResults(response));
          } else {
            setResults(formatResults(response));
          }
        }
      }
    };

    preloadResults();
  }, [form.getValues("idResultPreload")]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGetFieldsAnalysis = () => {
    setOpen(true);
  };

  const handleToReportUpdate = () => {
    if (form.getValues("idResultPreload")) {
      putResult(form.getValues("idResultPreload"), true);
      form.setValue("to_report", "to_report");
    }
  };

  const handleDownloadPDF = async () => {
    const currentTypeAnalysis = form.getValues("typeAnalysis");
    const typesAllAnalisis = form.getValues("analysis_type")?.map((item: any) => item.codigo);
    
    // Check if sample has both BT and BR
    const hasBothBTBR =
      typesAllAnalisis?.includes(KeysTabsAnalysis.BT) &&
      typesAllAnalisis?.includes(KeysTabsAnalysis.BR) &&
      (currentTypeAnalysis?.codigo === KeysTabsAnalysis.BT || currentTypeAnalysis?.codigo === KeysTabsAnalysis.BR);

    // Check if sample has both Sr89 and Sr90
    const hasBothSr89Sr90 =
      typesAllAnalisis?.includes(KeysTabsAnalysis.Sr89) &&
      typesAllAnalisis?.includes(KeysTabsAnalysis.Sr90) &&
      (currentTypeAnalysis?.codigo === KeysTabsAnalysis.Sr89 || currentTypeAnalysis?.codigo === KeysTabsAnalysis.Sr90);

    if (hasBothBTBR) {
      // Get all preparations from the store
      const allPreparations = useAnalysis.getState().preparationsList;
      
      // Find preparations for BT and BR
      const btPreparations = allPreparations.filter((prep) =>
        prep.analysis_type.some((analysis) => analysis.codigo === KeysTabsAnalysis.BT)
      );
      const brPreparations = allPreparations.filter((prep) =>
        prep.analysis_type.some((analysis) => analysis.codigo === KeysTabsAnalysis.BR)
      );

      // Helper function to extract all calculations from a preparation
      const getCalculationsFromPrep = (prep: any) => {
        const calculations: any[] = [];
        if (prep.aa_measures) {
          for (const measure of prep.aa_measures) {
            if (measure.countings) {
              for (const counting of measure.countings) {
                if (counting.calculations) {
                  calculations.push(...counting.calculations);
                }
              }
            }
          }
        }
        if (prep.countings) {
          for (const counting of prep.countings) {
            if (counting.calculations) {
              calculations.push(...counting.calculations);
            }
          }
        }
        return calculations;
      };

      // Get the current result being displayed (idResultPreload)
      const currentResultId = form.getValues("idResultPreload");
      
      // Find the units for the current calculation by searching in all preparations
      let currentUnits: string | undefined = undefined;
      for (const prep of allPreparations) {
        const calculations = getCalculationsFromPrep(prep);
        for (const calc of calculations) {
          if (calc.result?.id === currentResultId) {
            currentUnits = calc.units;
            break;
          }
        }
        if (currentUnits !== undefined) break;
      }

      // Helper function to select the calculation with matching units
      const selectCalculationByUnits = (calculations: any[], targetUnits?: string) => {
        if (calculations.length === 0) return undefined;
        
        // If we have units to match, filter by them
        if (targetUnits !== undefined) {
          const matchingUnitsCalcs = calculations.filter(
            calc => calc.units === targetUnits
          );
          if (matchingUnitsCalcs.length > 0) {
            // First, try to find one with to_report = true
            const withToReport = matchingUnitsCalcs.find(
              calc => calc.result?.to_reports === true
            );
            if (withToReport) return withToReport;
            // If no to_report, return the last one with matching units
            return matchingUnitsCalcs[matchingUnitsCalcs.length - 1];
          }else{
            return undefined;
          }
        }

        // Fallback: First, try to find one with to_report = true
        const withToReport = calculations.find(
          calc => calc.result?.to_reports === true
        );
        if (withToReport) return withToReport;

        // If no to_report, return the last one
        return calculations[calculations.length - 1];
      };

      // Determine which analysis type is currently being displayed
      const isCurrentBT = currentTypeAnalysis?.codigo === KeysTabsAnalysis.BT;
      const isCurrentBR = currentTypeAnalysis?.codigo === KeysTabsAnalysis.BR;

      let btResultId: number | undefined = undefined;
      let brResultId: number | undefined = undefined;
      
      if (isCurrentBT) {
        // Current is BT, use it as the primary result
        btResultId = currentResultId;

        // Find BR calculations and select the one with matching units
        const allBrCalculations = brPreparations.flatMap(getCalculationsFromPrep);
        const bestBrCalc = selectCalculationByUnits(allBrCalculations, currentUnits);
        brResultId = bestBrCalc?.result?.id;
      } else if (isCurrentBR) {
        // Current is BR, use it as the primary result
        brResultId = currentResultId;

        // Find BT calculations and select the one with matching units
        const allBtCalculations = btPreparations.flatMap(getCalculationsFromPrep);
        const bestBtCalc = selectCalculationByUnits(allBtCalculations, currentUnits);
        btResultId = bestBtCalc?.result?.id;
      }

      if (btResultId && brResultId) {
        // Fetch both results from API
        const [btResponse, brResponse] = await Promise.all([
          fetchCalculation(btResultId),
          fetchCalculation(brResultId)
        ]);

        if (btResponse && brResponse) {
          // Import filterAndSortData from helpers
          const { filterAndSortData } = await import("@app/pages/samples/utils/helpers");
          
          // Format BT data
          const btFormattedData: any = {
            ...btResponse,
            calculation_id: btResponse?.calculation?.id,
          };
          delete btFormattedData.calculation;
          const btData = filterAndSortData(btFormattedData, isCurrier, KeysTabsAnalysis.BT);
          
          // Format BR data
          const brFormattedData: any = {
            ...brResponse,
            calculation_id: brResponse?.calculation?.id,
          };
          delete brFormattedData.calculation;
          const brData = filterAndSortData(brFormattedData, isCurrier, KeysTabsAnalysis.BR);

          const dualResults = {
            BT: {
              results: formatResults(btResponse),
              outputData: btData.arrrayKeys,
              extendedLabels: btData.extendedLabelString,
            },
            BR: {
              results: formatResults(brResponse),
              outputData: brData.arrrayKeys,
              extendedLabels: brData.extendedLabelString,
            },
          };

          await PDFGeneratorService.downloadPDFDocument(
            { ...form.getValues(), ...sample },
            results,
            resultsAll,
            dualResults
          );
          return;
        }
      }
    }

    if (hasBothSr89Sr90) {
      // Get all preparations from the store
      const allPreparations = useAnalysis.getState().preparationsList;
      
      // Find preparations for Sr89 and Sr90
      const sr89Preparations = allPreparations.filter((prep) =>
        prep.analysis_type.some((analysis) => analysis.codigo === KeysTabsAnalysis.Sr89)
      );
      const sr90Preparations = allPreparations.filter((prep) =>
        prep.analysis_type.some((analysis) => analysis.codigo === KeysTabsAnalysis.Sr90)
      );

      // Helper function to extract all calculations from a preparation
      const getCalculationsFromPrep = (prep: any) => {
        const calculations: any[] = [];
        if (prep.aa_measures) {
          for (const measure of prep.aa_measures) {
            if (measure.countings) {
              for (const counting of measure.countings) {
                if (counting.calculations) {
                  calculations.push(...counting.calculations);
                }
              }
            }
          }
        }
        if (prep.countings) {
          for (const counting of prep.countings) {
            if (counting.calculations) {
              calculations.push(...counting.calculations);
            }
          }
        }
        return calculations;
      };

      // Get the current result being displayed (idResultPreload)
      const currentResultId = form.getValues("idResultPreload");
      
      // Find the units for the current calculation by searching in all preparations
      let currentUnits: string | undefined = undefined;
      let currentCalculation: any = undefined;
      for (const prep of allPreparations) {
        const calculations = getCalculationsFromPrep(prep);
        for (const calc of calculations) {
          if (calc.result?.id === currentResultId) {
            currentUnits = calc.units;
            currentCalculation = calc;
            break;
          }
        }
        if (currentUnits !== undefined) break;
      }

      // Helper function to select the calculation with matching units
      const selectCalculationByUnits = (calculations: any[], targetUnits?: string) => {
        if (calculations.length === 0) return undefined;
        
        // If we have units to match, filter by them
        if (targetUnits !== undefined) {
          const matchingUnitsCalcs = calculations.filter(
            calc => calc.units === targetUnits
          );
          if (matchingUnitsCalcs.length > 0) {
            // First, try to find one with to_report = true
            const withToReport = matchingUnitsCalcs.find(
              calc => calc.result?.to_reports === true
            );
            if (withToReport) return withToReport;
            // If no to_report, return the last one with matching units
            return matchingUnitsCalcs[matchingUnitsCalcs.length - 1];
          }else{
            return undefined;
          }
        }

        // Fallback: First, try to find one with to_report = true
        const withToReport = calculations.find(
          calc => calc.result?.to_reports === true
        );
        if (withToReport) return withToReport;

        // If no to_report, return the last one
        return calculations[calculations.length - 1];
      };

      // Determine which analysis type is currently being displayed
      const isCurrentSr89 = currentTypeAnalysis?.codigo === KeysTabsAnalysis.Sr89;
      const isCurrentSr90 = currentTypeAnalysis?.codigo === KeysTabsAnalysis.Sr90;

      let sr89ResultId: number | undefined = undefined;
      let sr90ResultId: number | undefined = undefined;

      if (isCurrentSr89) {
        // Current is Sr89, use it as the primary result
        sr89ResultId = currentResultId;

        // Find Sr90 calculations and select the one with matching units
        const allSr90Calculations = sr90Preparations.flatMap(getCalculationsFromPrep);
        const bestSr90Calc = selectCalculationByUnits(allSr90Calculations, currentUnits);
        sr90ResultId = bestSr90Calc?.result?.id;
      } else if (isCurrentSr90) {
        // Current is Sr90, use it as the primary result
        sr90ResultId = currentResultId;

        // Find Sr89 calculations and select the one with matching units
        const allSr89Calculations = sr89Preparations.flatMap(getCalculationsFromPrep);
        const bestSr89Calc = selectCalculationByUnits(allSr89Calculations, currentUnits);
        sr89ResultId = bestSr89Calc?.result?.id;
      }

      if (sr89ResultId && sr90ResultId) {
        // Fetch both results from API
        const [sr89Response, sr90Response] = await Promise.all([
          fetchCalculation(sr89ResultId),
          fetchCalculation(sr90ResultId)
        ]);

        if (sr89Response && sr90Response) {
          // Import filterAndSortData from helpers
          const { filterAndSortData } = await import("@app/pages/samples/utils/helpers");
          
          // Format Sr89 data
          const sr89FormattedData: any = {
            ...sr89Response,
            calculation_id: sr89Response?.calculation?.id,
          };
          delete sr89FormattedData.calculation;
          const sr89Data = filterAndSortData(sr89FormattedData, isCurrier, KeysTabsAnalysis.Sr89);
          
          // Format Sr90 data
          const sr90FormattedData: any = {
            ...sr90Response,
            calculation_id: sr90Response?.calculation?.id,
          };
          delete sr90FormattedData.calculation;
          const sr90Data = filterAndSortData(sr90FormattedData, isCurrier, KeysTabsAnalysis.Sr90);

          const dualResults = {
            Sr89: {
              results: formatResults(sr89Response),
              outputData: sr89Data.arrrayKeys,
              extendedLabels: sr89Data.extendedLabelString,
            },
            Sr90: {
              results: formatResults(sr90Response),
              outputData: sr90Data.arrrayKeys,
              extendedLabels: sr90Data.extendedLabelString,
            },
          };

          await PDFGeneratorService.downloadPDFDocument(
            { ...form.getValues(), ...sample },
            results,
            resultsAll,
            dualResults
          );
          return;
        }
      }
    }

    // Default: single analysis type PDF
    // For BPP, include analyst, procedure and background codes from calculation form
    const pdfData = { ...form.getValues(), ...sample };

    if (currentTypeAnalysis?.codigo === KeysTabsAnalysis.Ni63 || currentTypeAnalysis?.codigo === KeysTabsAnalysis.Fe55) {
      pdfData.cpm_white_quimioluminiscencia = form.getValues("cmp_white_quimioluminiscencia");
      pdfData.cpm_sample_quimioluminiscencia = form.getValues("cpm_sample_quimioluminiscencia");
    }
    
    if (currentTypeAnalysis?.codigo === KeysTabsAnalysis.BPP) {
      pdfData.calculation_analyst = form.getValues("analyst");
      pdfData.calculation_procedure_mlpma = form.getValues("calculation_procedure");
      pdfData.calculation_fondo = form.getValues("calculation_fondo");
      pdfData.fondo_alfa = form.getValues("fondo_alfa");
    }
    
    await PDFGeneratorService.downloadPDFDocument(
      pdfData,
      results,
      resultsAll
    );
  };

  const setTypeAnalisisMemo = useMemo(() => {
    const typeAnalysis = form.getValues("typeAnalysis");
    const codeTypeAnalisis = typeAnalysis?.codigo;
    if (
      codeTypeAnalisis === KeysTabsAnalysis.BT ||
      codeTypeAnalisis === KeysTabsAnalysis.BR
    ) {
      const typesAllAnalisis = form
        .getValues("analysis_type")
        ?.map((item: any) => item.codigo);

     /* if (
        typesAllAnalisis?.includes(KeysTabsAnalysis.BT) &&
        typesAllAnalisis?.includes(KeysTabsAnalysis.BR)
      ) {
        return { codigo: KeysTabsAnalysis.BTBR, descripcion: "BT - BR" };
      }*/
    }

    if (codeTypeAnalisis === KeysTabsAnalysis.AT) {
      const typePreparation = form.getValues("typePreparation");

      switch (typePreparation?.name) {
        case TYPE_ANALYSIS_ALFATOTAL.EVAPORATION:
          return { codigo: KeysTabsAnalysis.AT_E, descripcion: "AT - Evap." };

        case TYPE_ANALYSIS_ALFATOTAL.COPRECIPITATION:
          return { codigo: KeysTabsAnalysis.AT_C, descripcion: "AT - Coprec." };

        case TYPE_ANALYSIS_ALFATOTAL.PARTICLES:
          return { codigo: KeysTabsAnalysis.AT_PP, descripcion: "AT - PP" };
      }
    }

    return typeAnalysis;
  }, [form.getValues("typeAnalysis")]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {form.getValues("idResultPreload") &&
        !loadingResults &&
        results?.length > 0 && (
          <>
            <Grid
              container
              spacing={2}
              p={4}
              justifyContent="center"
              alignItems="end"
            >
              <Grid item xs={12} md={12} className="flexLeft">
                <Typography variant="h6" sx={styles.resultsTableHeader}>
                  RESULTADOS:
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}></Grid>
              <Grid item xs={12} md={6} className="flexCenter">
                <TableContainer component={Paper}>
                  <Table sx={styles.resultsTable}>
                    <TableBody>
                      {results.map((result, index) => (
                        <TableRow key={index}>
                          <TableCell
                            sx={{
                              ...styles.resultsTableCell,
                              ...styles.resultsTableCellLabel,
                            }}
                          >
                            {result.label}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...styles.resultsTableCell,
                              ...styles.resultsTableCellValue,
                            }}
                          >
                            {toExponentialUppercaseWhitZero(
                              Number(result.value),
                              2
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12} md={3}></Grid>

              <ResponsiveFormContainer columns={1} ml={0.5}>
                <Box display="flex" justifyContent="center" mt={2}>
                  <MCheckbox
                    key={JSON.stringify(form.watch("to_report"))}
                    name="to_report"
                    options={[
                      {
                        label: "Para informe",
                        value: "to_report",
                      },
                    ]}
                    multiple={false}
                    disabled={true}
                  />
                  <MButton
                    variant="contained"
                    color="primary"
                    text="Utilizar resultado en informe"
                    onClick={handleToReportUpdate}
                    disabled={form.getValues("to_report") === "to_report"}
                  />
                </Box>
              </ResponsiveFormContainer>

              <Grid
                item
                xs={12}
                md={12}
                display="flex"
                justifyContent="flex-end"
                gap={2}
              >
                <MButton
                  variant="outlined"
                  color="secondary"
                  text="Visualizar"
                  onClick={handleDownloadPDF}
                />
                <MButton
                  onClick={handleGetFieldsAnalysis}
                  text=" Datos de salida"
                  color="primary"
                />
              </Grid>
            </Grid>

            <DataModal
              open={open}
              handleClose={() => setOpen(false)}
              data={resultsAll}
              typeAnalysis={setTypeAnalisisMemo}
              isCurrier={isCurrier}
            />
          </>
        )}
    </>
  );
};

export default ResultsTable;
